import { defineStore } from 'pinia';

// Api Helper
import companyAPI from '@api/company';
import companyContactAPI from '@api/companyContact';
import companyBankAccountAPI from '@api/companyBankAccount';
import companyDepositFractionAPI from '@api/companyDepositFraction';
import companyDepositHoursAPI from '@/api/companyDepositHours';
import companyShowHideTravelHoursAPI from '@/api/companyShowHideTravelHours';
import companyShowHideTotalSpaceAPI from '@/api/companyShowHideTotalSpace';
import companyPerHourPricingAPI from '@/api/companyPerHourPricing';
import companyFinalPaymentTermsAPI from '@/api/companyFinalPaymentTerms';

export const useCompanyStore = defineStore('company', {
    state: () => {
        return {
            loading: false,
            companyId: null,
            data: null,
            settings: {
                bank_institutions: [],
                bank_details: null,
            },
            call_out_fee: [],
            contacts: [],
            predefined: {},
        };
    },
    actions: {
        async create(companyData) {
            try {
                this.loading = true;
                await companyAPI.create(companyData);
            } catch (e) {
                console.error('CompanyStore :: create', e);
                throw e;
            } finally {
                this.loading = false;
            }
        },

        async update(companyId, companyData) {
            try {
                this.loading = true;
                const companyUpdateResponse = await companyAPI.update(companyId, {
                    company_id: companyId,
                    ...companyData,
                    ...(companyData?.about && {
                        about: companyData.about.replace(/<\/?[^>]+>/gi, ''),
                    }),
                    ...(companyData?.description && {
                        description: companyData.description.replace(/<\/?[^>]+>/gi, ''),
                    }),
                });
                this.data = companyUpdateResponse.data.company;
                this.loading = false;
                return companyUpdateResponse.data.company;
            } catch (error) {
                this.loading = false;
                throw error;
            }
        },
        async updateSettings(companyId, companyData) {
            try {
                this.loading = true;
                const companyUpdateResponse = await companyAPI.updateSettings(companyId, {
                    company_id: companyId,
                    ...companyData,
                });
                this.settings = companyUpdateResponse.data.companySettings;
                return companyUpdateResponse.data.companySettings;
            } catch (e) {
                console.error('CompanyStore :: updateSettings', e);
                throw e;
            } finally {
                this.loading = false;
            }
        },

        setCompanyId(companyId) {
            this.companyId = companyId;
        },

        async retrieve(companyId, query) {
            try {
                this.loading = true;
                if (this.companyId) this.setCompanyId(companyId);
                const response = await companyAPI.retrieve(this.companyId, query);
                this.data = response.data.data;
                this.companyId = response.data.data.id;
                if (this.data.about) {
                    this.data.about = this.data.about.replace(/<\/?[^>]+>/gi, '');
                }
                if (this.data.description) {
                    this.data.description = this.data.description.replace(/<\/?[^>]+>/gi, '');
                }
                // this.data.description
                this.loading = false;
            } catch (error) {
                this.loading = false;
                // let the form component display the error
                throw error;
            }
        },

        async retrieveSettings(companyId, query = {}) {
            try {
                this.loading = true;
                const response = await companyAPI.retrieveSettings(companyId, query);

                // Ensure we have companySettings in the response
                if (response.data && response.data.companySettings) {
                    // Merge with existing settings to preserve bank_institutions and other data
                    this.settings = {
                        ...this.settings,
                        ...response.data.companySettings,
                    };

                    return response.data.companySettings;
                } else {
                    return null;
                }
            } catch (e) {
                console.error('Company settings: ', e);
                throw e;
            } finally {
                this.loading = false;
            }
        },

        async retrieveLogo(companyId = this.getCompanyId) {
            try {
                const {
                    data: { data },
                } = await window.tenantAxios.get(`/companies/${companyId}/logo`);
                this.data.logo = data.url;
            } catch (e) {
                console.error('CompanyStore :: retrieveLogo', e);
            }
        },

        async retrieveContacts() {
            try {
                this.loading = true;
                this.contacts = await companyContactAPI.index(this.companyId);
            } catch (error) {
                this.loading = false;
                // let the form component display the error
                throw error;
            }
        },

        async retrievePredefined() {
            const response = await window.tenantAxios.get('/companies/predefined');
            this.predefined = response.data;
        },

        async updateContactHours(companyId, contactHours) {
            const response = await window.tenantAxios.put(`/companies/${companyId}/contact-hours`, contactHours);
            this.settings.contact_hours = response.data.contact_hours;
        },

        // Retrieve bank account for a company
        async retrieveBankAccount(companyId) {
            try {
                this.loading = true;
                const response = await companyBankAccountAPI.retrieveBankAccount(companyId);
                this.settings.bank_details = response.data;
                return response.data;
            } catch (error) {
                this.loading = false;
                throw error;
            } finally {
                this.loading = false;
            }
        },

        // Update company's bank account
        async updateBankAccount(companyId, bankAccountData) {
            try {
                this.loading = true;
                const response = await companyBankAccountAPI.update(companyId, bankAccountData);
                this.settings.bank_details = response.data.bank_details;
            } catch (error) {
                this.loading = false;
                throw error;
            } finally {
                this.loading = false;
            }
        },

        // Retrieve deposit fraction setting for a company
        async retrieveDepositFraction(companyId) {
            try {
                this.loading = true;
                const response = await companyDepositFractionAPI.retrieveDepositFraction(companyId);
                this.settings.deposit_fraction = response.data;
                return response.data;
            } catch (error) {
                this.loading = false;
                throw error;
            } finally {
                this.loading = false;
            }
        },

        // Update company's deposit fraction for interstate moves
        async updateDepositFraction(companyId, depositFraction) {
            try {
                this.loading = true;
                const response = await companyDepositFractionAPI.updateDepositFraction(companyId, depositFraction);
                this.settings.deposit_fraction = response.data.deposit_fraction;
            } catch (error) {
                this.loading = false;
                throw error;
            } finally {
                this.loading = false;
            }
        },

        // Retrieve card fee settings for a company
        async retrieveCardFee(companyId) {
            try {
                this.loading = true;
                const response = await window.tenantAxios.get(`/company/${companyId}/card-fee`);
                this.settings = response.data;
                return response.data;
            } catch (error) {
                this.loading = false;
                throw error;
            } finally {
                this.loading = false;
            }
        },

        // Update card fee settings for a company
        async updateCardFee(companyId, cardFee) {
            try {
                this.loading = true;
                const response = await window.tenantAxios.patch(`/company/${companyId}/card-fee`, cardFee);
                this.settings = response.data;
            } catch (error) {
                this.loading = false;
                throw error;
            } finally {
                this.loading = false;
            }
        },

        // Retrieve deposit fraction setting for a company
        async retrieveDepositHours(companyId) {
            try {
                this.loading = true;
                const response = await companyDepositHoursAPI.retrieveDepositHours(companyId);
                this.settings.deposit_hours = response.data;
                return response.data;
            } catch (error) {
                this.loading = false;
                throw error;
            } finally {
                this.loading = false;
            }
        },

        async updateDefaultHoursDeposit(companyId, updateDepositHours) {
            try {
                this.loading = true;
                const response = await companyDepositHoursAPI.updateDepositHours(companyId, updateDepositHours);
                this.settings.deposit_hours = response.data;
            } catch (error) {
                this.loading = false;
                throw error;
            } finally {
                this.loading = false;
            }
        },

        // Retrieve deposit fraction setting for a company
        async retrieveShowTravelTime(companyId) {
            try {
                this.loading = true;
                const response = await companyShowHideTravelHoursAPI.retrieveShowTravelTime(companyId);
                this.settings.show_travel_time = response.data;
                return response.data;
            } catch (error) {
                this.loading = false;
                throw error;
            } finally {
                this.loading = false;
            }
        },
        async updateShowTravelTime(companyId, showTravelTimeData) {
            try {
                this.loading = true;
                const response = await companyShowHideTravelHoursAPI.updateShowTravelTime(
                    companyId,
                    showTravelTimeData,
                );
                this.settings.show_travel_time = response.data;
            } catch (error) {
                this.loading = false;
                throw error;
            } finally {
                this.loading = false;
            }
        },
        // Retrieve per hour pricing setting for a company
        async retrievePerHourPricing(companyId) {
            try {
                this.loading = true;
                const response = await companyPerHourPricingAPI.retrievePerHourPricing(companyId);
                this.settings.per_hour_pricing = response.data;
                return response.data;
            } catch (error) {
                this.loading = false;
                throw error;
            } finally {
                this.loading = false;
            }
        },
        async updatePerHourPricing(companyId, perHourPricingData) {
            try {
                this.loading = true;
                const response = await companyPerHourPricingAPI.updatePerHourPricing(companyId, perHourPricingData);
                this.settings.per_hour_pricing = response.data;
            } catch (error) {
                this.loading = false;
                throw error;
            } finally {
                this.loading = false;
            }
        },
        async retrieveShowTotalSpace(companyId) {
            try {
                this.loading = true;
                const response = await companyShowHideTotalSpaceAPI.retrieveShowTotalSpace(companyId);
                this.settings.show_total_space = response.data;
                return response.data;
            } catch (error) {
                this.loading = false;
                throw error;
            } finally {
                this.loading = false;
            }
        },
        async updateShowTotalSpace(companyId, showTotalSpaceData) {
            try {
                this.loading = true;
                const response = await companyShowHideTotalSpaceAPI.updateShowTotalSpace(companyId, showTotalSpaceData);
                this.settings.show_total_space = response.data;
            } catch (error) {
                this.loading = false;
                throw error;
            } finally {
                this.loading = false;
            }
        },
        async retrieveFinalPaymentTerms(companyId) {
            try {
                this.loading = true;
                const response = await companyFinalPaymentTermsAPI.retrieveFinalPaymentTerms(companyId);
                this.settings.final_payment_terms = response.data;
                return response.data;
            } catch (error) {
                this.loading = false;
                throw error;
            } finally {
                this.loading = false;
            }
        },
        async updateFinalPaymentTerms(companyId, finalPaymentTermsData) {
            try {
                this.loading = true;
                const response = await companyFinalPaymentTermsAPI.updateFinalPaymentTerms(
                    companyId,
                    finalPaymentTermsData,
                );
                this.settings.final_payment_terms = response.data;
            } catch (error) {
                this.loading = false;
                throw error;
            } finally {
                this.loading = false;
            }
        },

        // Retrieve custom additional charges for a company
        async retrieveCustomAdditionalCharges() {
            const response = await window.tenantAxios.get('interstate-price-items/tenant-items?type=CUSTOM');
            return response.data.data;
        },

        async updateCallOutFee(companyId, callOutFee) {
            try {
                this.loading = true;
                const response = await window.tenantAxios.patch(`/company/${companyId}/call-out-fee`, callOutFee);
                // Update settings with the response data so getCallOutFeeSettings getter reflects the changes
                // Handle both nested (response.data.data) and direct (response.data) response structures
                const responseData = response.data?.data || response.data;
                if (responseData) {
                    this.settings.call_out_fee_type = responseData.call_out_fee_type;
                    this.settings.call_out_fee_static_amount = responseData.call_out_fee_static_amount;
                    this.settings.call_out_fee_dynamic_minus_minutes = responseData.call_out_fee_dynamic_minus_minutes;
                    this.settings.travel_time_type = responseData.travel_time_type;
                }
                // Also update call_out_fee for consistency
                if (responseData) {
                    this.call_out_fee = responseData;
                }
                return response.data;
            } catch (error) {
                this.loading = false;
                throw error;
            } finally {
                this.loading = false;
            }
        },

        async retrieveCallOutFee(companyId) {
            const response = await window.tenantAxios.get(`/company/${companyId}/call-out-fee`);
            this.call_out_fee = response.data.data;
            // Also update settings so getCallOutFeeSettings getter reflects the changes
            if (response.data.data) {
                this.settings.call_out_fee_type = response.data.data.call_out_fee_type;
                this.settings.call_out_fee_static_amount = response.data.data.call_out_fee_static_amount;
                this.settings.call_out_fee_dynamic_minus_minutes =
                    response.data.data.call_out_fee_dynamic_minus_minutes;
                this.settings.travel_time_type = response.data.data.travel_time_type;
            }
            return response.data.data;
        },

        // Retrieve movers settings for a company
        async retrieveMoverSettings(companyId) {
            try {
                this.loading = true;
                const response = await window.tenantAxios.get(`/company/${companyId}/movers-settings`);
                this.settings.local_movers = response.data.local_movers;
                return response.data;
            } catch (error) {
                this.loading = false;
                throw error;
            } finally {
                this.loading = false;
            }
        },

        // Update movers settings for a company
        async updateMoverSettings(companyId, moversSettings) {
            try {
                this.loading = true;
                const response = await window.tenantAxios.patch(
                    `/company/${companyId}/movers-settings`,
                    moversSettings,
                );
                this.settings.local_movers = response.data.local_movers;
                return response.data;
            } catch (error) {
                this.loading = false;
                throw error;
            } finally {
                this.loading = false;
            }
        },
        // Retrieve local/interstate threshold settings for a company
        async retrieveLocalInterstateThresholdSettings(companyId) {
            try {
                this.loading = true;
                const response = await window.tenantAxios.get(`/company/${companyId}/local-interstate-threshold`);
                this.settings.local_interstate_threshold_km = response.data.local_interstate_threshold_km;
                return response.data;
            } catch (error) {
                this.loading = false;
                throw error;
            } finally {
                this.loading = false;
            }
        },

        // Update local/interstate threshold settings for a company
        async updateLocalInterstateThresholdSettings(companyId, localInterstateThresholdSettings) {
            try {
                this.loading = true;
                const response = await window.tenantAxios.patch(
                    `/company/${companyId}/local-interstate-threshold`,
                    localInterstateThresholdSettings,
                );
                this.settings.local_interstate_threshold_km = response.data.local_interstate_threshold_km;
                return response.data;
            } catch (error) {
                this.loading = false;
                throw error;
            } finally {
                this.loading = false;
            }
        },

        // Retrieve notice settings for a company
        async retrieveNoticeSettings(companyId) {
            try {
                this.loading = true;
                const response = await window.tenantAxios.get(`/company/${companyId}/notice-settings`);
                this.settings.notice_enabled = response.data;
                this.settings.notice_title = response.data.notice_title;
                this.settings.notice_body = response.data.notice_body;
                return response.data;
            } catch (error) {
                this.loading = false;
                throw error;
            } finally {
                this.loading = false;
            }
        },

        // Update notice settings for a company
        async updateNoticeSettings(companyId, noticeSettings) {
            try {
                this.loading = true;
                const response = await window.tenantAxios.patch(
                    `/company/${companyId}/notice-settings`,
                    noticeSettings,
                );
                this.settings.notice_enabled = response.data;
                this.settings.notice_title = response.data.notice_title;
                this.settings.notice_body = response.data.notice_body;
                return response.data;
            } catch (error) {
                this.loading = false;
                throw error;
            } finally {
                this.loading = false;
            }
        },

        // Retrieve move notes settings for a company
        async retrieveMoveNotesSettings(companyId) {
            try {
                this.loading = true;
                const response = await window.tenantAxios.get(`/company/${companyId}/move-notes-settings`);
                this.settings.interstate_move_note = response.data.data.interstate_move_note;
                this.settings.local_move_note = response.data.data.local_move_note;
                return response.data.data;
            } catch (error) {
                this.loading = false;
                throw error;
            } finally {
                this.loading = false;
            }
        },

        // Update move notes settings for a company
        async updateMoveNotesSettings(companyId, moveNotesSettings) {
            try {
                this.loading = true;
                const response = await window.tenantAxios.patch(
                    `/company/${companyId}/move-notes-settings`,
                    moveNotesSettings,
                );
                this.settings.interstate_move_note = response.data.data.interstate_move_note;
                this.settings.local_move_note = response.data.data.local_move_note;
                return response.data.data;
            } catch (error) {
                this.loading = false;
                throw error;
            } finally {
                this.loading = false;
            }
        },

        // Retrieve bank deposit visibility settings for a company
        async retrieveBankDepositVisibility(companyId) {
            try {
                this.loading = true;
                const response = await window.tenantAxios.get(`/company/${companyId}/bank-deposit-visibility`);
                this.settings.show_bank_deposit = response.data.show_bank_deposit;
                return response.data;
            } catch (error) {
                this.loading = false;
                throw error;
            } finally {
                this.loading = false;
            }
        },

        // Update bank deposit visibility settings for a company
        async updateBankDepositVisibility(companyId, bankDepositVisibility) {
            try {
                this.loading = true;
                const response = await window.tenantAxios.patch(
                    `/company/${companyId}/bank-deposit-visibility`,
                    bankDepositVisibility,
                );
                this.settings.show_bank_deposit = response.data.show_bank_deposit;
                return response.data;
            } catch (error) {
                this.loading = false;
                throw error;
            } finally {
                this.loading = false;
            }
        },
    },
    getters: {
        getName() {
            return this.data?.name;
        },

        getProfileSlug: (state) => {
            return state.data?.profile_slug;
        },

        getCompanyId: (state) => {
            return state.data?.id;
        },

        getDocuments() {
            return this.data?.documents;
        },

        hasDocuments() {
            return this.data?.documents?.length > 0;
        },

        getInterstatePriceValues() {
            return this.data?.interstatePriceValues;
        },

        hasInterstatePriceValues() {
            return this.data?.interstatePriceValues?.length > 0;
        },
        getCompany: (state) => {
            return state.data;
        },
        getStatus: (state) => {
            return state.data?.status;
        },
        getProfileNote: (state) => {
            return state.data?.notes;
        },
        getAdditionalCharges: (state) => {
            return state.data?.additional_charges;
        },
        getStatusList(state) {
            return state.predefined.statuses;
        },
        getBusinessTypeList(state) {
            return state.predefined.business_type;
        },
        getCompanyTypeList(state) {
            return state.predefined.types;
        },
        getBankInstitutions(state) {
            return state.settings?.bank_institutions;
        },
        getCallOutFeeSettings(state) {
            return {
                call_out_fee_type: state.settings?.call_out_fee_type,
                call_out_fee_static_amount: state.settings?.call_out_fee_static_amount,
                call_out_fee_dynamic_minus_minutes: state.settings?.call_out_fee_dynamic_minus_minutes,
                travel_time_type: state.settings?.travel_time_type,
            };
        },
    },
});
